import { Text } from "@/components/Text";
import { formatDate } from "@/i18n/format";
import { wallClock } from "./calendarModel";

/** How something on the grid looks: someone's busy time, a maybe, a time to pick, or the meeting itself. */
export type GridTone = "busy" | "tentative" | "slot" | "picked";

export interface GridItem {
  id: string;
  date: string;
  /** Minutes since midnight. */
  from: number;
  to: number;
  tone: GridTone;
  /** Shown inside when there's room (a meeting's title). */
  label?: string;
  /** Read out and shown on hover. */
  title: string;
  /** Makes it a button (a suggested time). */
  onSelect?: () => void;
}

const TONES: Record<GridTone, string> = {
  busy: "bg-surface-tertiary border border-border-strong",
  tentative: "bg-warning-subtle border border-dashed border-warning",
  slot: "bg-info-subtle border border-info cursor-pointer hover:bg-info-fill",
  picked: "bg-brand-primary-subtle border-2 border-brand-primary",
};

const WEEKDAY: Intl.DateTimeFormatOptions = { weekday: "short" };
const DATE: Intl.DateTimeFormatOptions = { month: "numeric", day: "numeric" };
const HOUR: Intl.DateTimeFormatOptions = { hour: "numeric" };

/**
 * A small week (or day) of a calendar (MHS-969): a column per day, hour lines, and blocks placed by time. It shows
 * where things fall, not a full calendar: no scrolling, no dragging.
 */
export function TimeGrid({
  days,
  range,
  items,
  label,
  rowHeight = 28,
}: Readonly<{
  days: string[];
  /** First and last hour shown. */
  range: [number, number];
  items: GridItem[];
  /** What the grid shows, for screen readers ("Dana's week"). */
  label: string;
  /** Height of an hour, in pixels. */
  rowHeight?: number;
}>) {
  const [lo, hi] = range;
  const span = (hi - lo) * 60;
  const hours = Array.from({ length: hi - lo }, (_, i) => lo + i);
  const top = (minutes: number) => `${((Math.max(minutes, lo * 60) - lo * 60) / span) * 100}%`;
  const height = (from: number, to: number) => `${((Math.min(to, hi * 60) - Math.max(from, lo * 60)) / span) * 100}%`;
  return (
    <figure aria-label={label} className="w-full overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `3.25rem repeat(${days.length}, minmax(0, 1fr))` }}>
        <span />
        {days.map((date) => (
          <Text key={date} as="span" variant="caption" tone="tertiary" className="flex flex-col items-center pb-1">
            <span>{formatDate(wallClock(`${date}T00:00`), WEEKDAY)}</span>
            <span>{formatDate(wallClock(`${date}T00:00`), DATE)}</span>
          </Text>
        ))}
        <div className="relative" style={{ height: (hi - lo) * rowHeight }}>
          {hours
            .filter((h) => h % 2 === lo % 2)
            .map((h) => (
              <span
                key={h}
                className="absolute right-1.5 -translate-y-1/2 whitespace-nowrap leading-none"
                style={{ top: top(h * 60) }}
              >
                <Text as="span" variant="caption" tone="tertiary">
                  {formatDate(wallClock(`2000-01-01T${String(h).padStart(2, "0")}:00`), HOUR)}
                </Text>
              </span>
            ))}
        </div>
        {days.map((date) => (
          <div
            key={date}
            className="relative border-border-subtle border-l bg-surface-primary"
            style={{ height: (hi - lo) * rowHeight }}
          >
            {hours.map((h) => (
              <div
                key={h}
                aria-hidden="true"
                className="absolute inset-x-0 border-border-subtle border-t"
                style={{ top: top(h * 60) }}
              />
            ))}
            {laidOut(items.filter((item) => item.date === date && item.to > lo * 60 && item.from < hi * 60)).map(
              ({ item, lane, lanes }) => (
                <Block
                  key={item.id}
                  item={item}
                  top={top(item.from)}
                  height={height(item.from, item.to)}
                  left={`${(lane / lanes) * 100}%`}
                  width={`${100 / lanes}%`}
                  roomy={((Math.min(item.to, hi * 60) - Math.max(item.from, lo * 60)) / 60) * rowHeight >= MIN_LABEL_PX}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </figure>
  );
}

// Shorter than this, a block shows no title (it's on hover and read out).
const MIN_LABEL_PX = 16;

interface Placed {
  item: GridItem;
  lane: number;
  lanes: number;
}

/**
 * Meetings that overlap sit side by side, as on a calendar: each group of overlapping blocks is split into lanes. A
 * time to pick (or the meeting being scheduled) spans the whole day on top, so it's always visible and clickable.
 */
export function laidOut(items: GridItem[]): Placed[] {
  const busy = items
    .filter((i) => i.tone === "busy" || i.tone === "tentative")
    .sort((a, b) => a.from - b.from || b.to - a.to);
  const placed: Placed[] = [];
  let group: Placed[] = [];
  let groupEnd = -1;
  const close = () => {
    const lanes = Math.max(1, ...group.map((p) => p.lane + 1));
    for (const p of group) p.lanes = lanes;
    group = [];
  };
  for (const item of busy) {
    if (item.from >= groupEnd) close();
    const taken = new Set(group.filter((p) => p.item.to > item.from).map((p) => p.lane));
    let lane = 0;
    while (taken.has(lane)) lane++;
    const entry = { item, lane, lanes: 1 };
    group.push(entry);
    placed.push(entry);
    groupEnd = Math.max(groupEnd, item.to);
  }
  close();
  const onTop = items
    .filter((i) => i.tone === "slot" || i.tone === "picked")
    .map((item) => ({ item, lane: 0, lanes: 1 }));
  return [...placed, ...onTop];
}

function Block({
  item,
  top,
  height,
  left,
  width,
  roomy,
}: Readonly<{ item: GridItem; top: string; height: string; left: string; width: string; roomy: boolean }>) {
  const className = `absolute overflow-hidden rounded-sm px-1 text-left ${TONES[item.tone]}`;
  const content =
    item.label && roomy ? (
      <Text as="span" variant="caption" className="block truncate leading-tight">
        {item.label}
      </Text>
    ) : null;
  if (item.onSelect) {
    return (
      <button
        type="button"
        aria-label={item.title}
        title={item.title}
        className={`btn-focus-ring z-10 ${className}`}
        style={{ top, height, left: `calc(${left} + 2px)`, width: `calc(${width} - 4px)` }}
        onClick={item.onSelect}
      >
        {content}
      </button>
    );
  }
  return (
    <div
      title={item.title}
      className={item.tone === "picked" ? `z-10 ${className}` : className}
      style={{ top, height, left: `calc(${left} + 1px)`, width: `calc(${width} - 2px)` }}
    >
      <span className="sr-only">{item.title}</span>
      {content}
    </div>
  );
}
